import { NAMING_PATTERN } from '@constants/naming-validation';
import { Translations } from '@domain/admission';
import { AdmissionGroup } from '@domain/admission-group';
import { getLanguageTranslationKey } from '@utils/get-language-trandlation-key/get-language-translation-key';
import { TFunction } from 'i18next';

export const GROUP_NAME_PATTERN = NAMING_PATTERN;

type DuplicateCheckProps = {
  value: string;
  language: string;
  sectionGroups: AdmissionGroup[];
  excludeGroupId?: string;
};

export const hasDuplicateGroupName = ({
  value,
  language,
  sectionGroups,
  excludeGroupId,
}: DuplicateCheckProps): boolean => {
  const normalized = value.trim().toLowerCase();

  return sectionGroups.some((group) => {
    const groupName = group.name?.[language];
    if (!groupName) return false;

    return group.id !== excludeGroupId && groupName.trim().toLowerCase() === normalized;
  });
};

type CreateValidationProps = {
  sectionGroups: AdmissionGroup[];
  t: TFunction;
  excludeGroupId?: string;
};

/**
 * Returns the `validation` object expected by EditFieldModal
 * (pattern + validate) modals to validate group names.
 */
export const createGroupNameValidation = ({ sectionGroups, t, excludeGroupId }: CreateValidationProps) => ({
  pattern: {
    value: GROUP_NAME_PATTERN,
    message: t('admissionGroups:inputInvalidCharactersMessage'),
  },
  validate: (value: string, language: string, _allValues: Translations) => {
    if (!value) return true;

    const trimmed = value.trim();

    if (!GROUP_NAME_PATTERN.test(trimmed)) {
      return t('admissionGroups:inputInvalidCharactersMessage');
    }

    const hasDuplicate = hasDuplicateGroupName({
      value: trimmed,
      language,
      sectionGroups,
      excludeGroupId,
    });

    if (!hasDuplicate) return true;

    return t('admissionGroups:groupModalDuplicateNameError', {
      language: t(getLanguageTranslationKey(language)).toLowerCase(),
    });
  },
});

type SelectionValidationProps = {
  name: string;
  language: string;
  sectionGroups: AdmissionGroup[];
  t: TFunction;
  excludeGroupId?: string;
  tRequiredKey?: string; // ej: 'errors:fieldEmptyError'
};

/**
 * Version designed for AdmissionCard + RadioListModal: returns the error message
 * (or empty string) for a specific name and language.
 */
export const getGroupNameErrorForSelection = ({
  name,
  language,
  sectionGroups,
  t,
  excludeGroupId,
  tRequiredKey,
}: SelectionValidationProps): string => {
  const trimmed = name.trim();

  if (!trimmed) {
    return tRequiredKey ? t(tRequiredKey) : '';
  }

  if (!GROUP_NAME_PATTERN.test(trimmed)) {
    return t('admissionGroups:inputInvalidCharactersMessage');
  }

  const hasDuplicate = hasDuplicateGroupName({
    value: trimmed,
    language,
    sectionGroups,
    excludeGroupId,
  });

  if (hasDuplicate) {
    return t('admissionGroups:groupModalDuplicateNameError', {
      language: t(getLanguageTranslationKey(language)).toLowerCase(),
    });
  }

  return '';
};
